/**
 * @license
 * SPDX-License-Identifier: Apache-2.0
 */

import { Tenant, UserProfile, Opportunity, Application, Placement, StudentTask, AttendanceReport, SystemLog } from '../types';

export const DEFAULT_TENANTS: Tenant[] = [];

export const MOCK_USERS: UserProfile[] = [
  {
    uid: 'super-joshua',
    name: 'Joshua (Superadmin)',
    email: 'joshua@losonova.com',
    role: 'superadmin',
    tenantId: 'global',
    avatar: '🛡️'
  },
  {
    uid: 'regional-sarah',
    name: 'Sarah Jenkins (Region 4 Admin)',
    email: 'sarah.reg@example.com',
    role: 'regional_admin',
    tenantId: 'apextech',
    region: 'Northeast Region',
    avatar: '📐'
  },
  {
    uid: 'regional-bill',
    name: 'Bill Gately (Metro Central Admin)',
    email: 'bill.reg@example.com',
    role: 'regional_admin',
    tenantId: 'springfield',
    region: 'Metro Central',
    avatar: '🧭'
  },
  {
    uid: 'org-apex',
    name: 'Dan Foster (Talent Coordinator)',
    email: 'dan.corp@example.com',
    role: 'organization',
    tenantId: 'spartanburg',
    extendedRegions: ['charleston', 'greenville', 'horry', 'richland'],
    orgName: 'Apex Tech Solutions',
    orgTeamId: 'org-apex',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://apextech.com',
    region: 'Spartanburg Region',
    avatar: '💼'
  },
  {
    uid: 'org-upstate-auto',
    name: 'Marcus Vance (Engineering Lead)',
    email: 'marcus@upstateauto.com',
    role: 'organization',
    tenantId: 'spartanburg',
    orgName: 'Upstate Auto Engineering Company',
    orgTeamId: 'org-upstate-auto',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.upstateautoengineering.com',
    region: 'Spartanburg Region',
    avatar: '⚙️'
  },
  {
    uid: 'org-upstate-healing',
    name: 'Dr. Sarah Lin (Clinical Advisor)',
    email: 'slin@upstatehealing.org',
    role: 'organization',
    tenantId: 'spartanburg',
    orgName: 'Upstate Healing Healthcare',
    orgTeamId: 'org-upstate-healing',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.upstatehealing.org',
    region: 'Spartanburg Region',
    avatar: '🩺'
  },
  {
    uid: 'org-novapack',
    name: 'Elena Rostova (Operations Manager)',
    email: 'elena@novapack.com',
    role: 'organization',
    tenantId: 'spartanburg',
    orgName: 'NovaPack Advanced Packaging',
    orgTeamId: 'org-novapack',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.novapack.com',
    region: 'Spartanburg Region',
    avatar: '📦'
  },
  {
    uid: 'org-precision',
    name: 'Robert Hayes (Plant Director)',
    email: 'rhayes@precisiondynamics.com',
    role: 'organization',
    tenantId: 'spartanburg',
    extendedRegions: ['union'],
    orgName: 'Precision Dynamics',
    orgTeamId: 'org-precision',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.precisiondynamics.com',
    region: 'Spartanburg Region',
    avatar: '🔧'
  },
  {
    uid: 'org-hospital',
    name: 'Dr. Helen Green (Residency Director)',
    email: 'doctor.green@example.com',
    role: 'organization',
    tenantId: 'greenville',
    orgName: 'Valley Medical Center',
    orgTeamId: 'org-hospital',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.valleymed.org',
    region: 'Greenville Region',
    avatar: '🏥'
  },
  {
    uid: 'org-greenville-biotech',
    name: 'Dr. Alan Vance (Lead Researcher)',
    email: 'alan@greenvillebiotech.com',
    role: 'organization',
    tenantId: 'greenville',
    orgName: 'Greenville BioTech Labs',
    orgTeamId: 'org-greenville-biotech',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.greenvillebiotech.com',
    region: 'Greenville Region',
    avatar: '🧬'
  },
  {
    uid: 'org-charleston-maritime',
    name: 'Captain Thomas Reed (Port Operations)',
    email: 'treed@charlestonmaritime.com',
    role: 'organization',
    tenantId: 'charleston',
    orgName: 'Charleston Maritime Logistics',
    orgTeamId: 'org-charleston-maritime',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.charlestonmaritime.com',
    region: 'Charleston Region',
    avatar: '🚢'
  },
  {
    uid: 'org-grandstrand-hospitality',
    name: 'Samantha Ross (Guest Services)',
    email: 'sross@grandstrandhospitality.com',
    role: 'organization',
    tenantId: 'horry',
    orgName: 'Grand Strand Hospitality Group',
    orgTeamId: 'org-grandstrand-hospitality',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.grandstrandhospitality.com',
    region: 'Horry Region',
    avatar: '🏖️'
  },
  {
    uid: 'org-palmetto-finance',
    name: 'David Sterling (Financial Director)',
    email: 'dsterling@palmettofinancial.com',
    role: 'organization',
    tenantId: 'richland',
    orgName: 'Palmetto Financial Group',
    orgTeamId: 'org-palmetto-finance',
    isOrgLead: true,
    showInParticipatingOrgs: true,
    website: 'https://www.palmettofinancial.com',
    region: 'Richland Region',
    avatar: '📊'
  },
  {
    uid: 'advisor-jane',
    name: 'Jane Smith (Academic Advisor)',
    email: 'jane.advisor@example.com',
    role: 'advisor',
    tenantId: 'spartanburg',
    school: 'James F. Byrnes High School (spartanburg)',
    schoolsServed: ['Spartanburg High School (spartanburg)', 'R. D. Anderson Applied Tech (spartanburg)'],
    avatar: '👩‍🏫',
    profileComplete: true
  },
  {
    uid: 'student-sam',
    name: 'Samuel Carter (Cloud Intern)',
    email: 'student.sam@example.com',
    role: 'student',
    tenantId: 'apextech',
    region: 'Northeast Region',
    parentId: 'parent-pam',
    district: 'Spartanburg School District 7 (spartanburg)',
    school: 'R. D. Anderson Applied Tech (spartanburg)',
    avatar: '🎒',
    ferpaConsent: {
      granted: true,
      date: '2026-05-15',
      version: '1.0'
    }
  },
  {
    uid: 'student-alex',
    name: 'Alex Rivera (Allied Health Aide)',
    email: 'student.alex@example.com',
    role: 'student',
    tenantId: 'valleyhealth',
    region: 'Metro Central',
    parentId: 'parent-pat',
    district: 'General School District',
    school: 'Valley View High School (training)',
    avatar: '🩺',
    ferpaConsent: {
      granted: true,
      date: '2026-05-16',
      version: '1.0'
    }
  },
  {
    uid: 'student-lily',
    name: 'Lily Watson',
    email: 'lily@example.com',
    role: 'student',
    tenantId: 'apextech',
    region: 'Northeast Region',
    district: 'Spartanburg School District 7 (spartanburg)',
    school: 'R. D. Anderson Applied Tech (spartanburg)',
    avatar: '🎒',
    ferpaConsent: {
      granted: true,
      date: '2026-05-17',
      version: '1.0'
    }
  },
  {
    uid: 'student-max',
    name: 'Max Peterson',
    email: 'max@example.com',
    role: 'student',
    tenantId: 'apextech',
    region: 'Northeast Region',
    district: 'Spartanburg School District 7 (spartanburg)',
    school: 'R. D. Anderson Applied Tech (spartanburg)',
    avatar: '🎒'
  },
  {
    uid: 'student-john',
    name: 'John Doe',
    email: 'john@example.com',
    role: 'student',
    tenantId: 'apextech',
    region: 'Northeast Region',
    district: 'Spartanburg School District 7 (spartanburg)',
    school: 'R. D. Anderson Applied Tech (spartanburg)',
    avatar: '🎒'
  },
  {
    uid: 'mentor-mary',
    name: 'Mary Vance (Cloud Architect)',
    email: 'mentor.mary@example.com',
    role: 'regional_admin',
    tenantId: 'apextech',
    district: 'Spartanburg School District 7 (spartanburg)',
    school: 'R. D. Anderson Applied Tech (spartanburg)',
    avatar: '👩‍🏫'
  },
  {
    uid: 'mentor-mark',
    name: 'Mark Ortiz (Cardiology Lead)',
    email: 'mentor.mark@example.com',
    role: 'regional_admin',
    tenantId: 'valleyhealth',
    district: 'General School District',
    school: 'Valley View High School (training)',
    avatar: '👨‍⚕️'
  },
  {
    uid: 'parent-pam',
    name: 'Pamela Carter',
    email: 'parent.pam@example.com',
    role: 'parent',
    tenantId: 'apextech',
    childId: 'student-sam',
    avatar: '👩‍👦'
  },
  {
    uid: 'parent-pat',
    name: 'Patricia Rivera',
    email: 'parent.pat@example.com',
    role: 'parent',
    tenantId: 'valleyhealth',
    childId: 'student-alex',
    avatar: '👩‍👧'
  }
];

export const MOCK_OPPORTUNITIES: Opportunity[] = [
  {
    id: 'opp-1',
    tenantId: 'spartanburg',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'Junior Cloud Infrastructure Intern',
    description: 'Learn dynamic Kubernetes orchestration, AWS resource provisioning pipelines, and structured Terraform infrastructure-as-code scripting. Shadow the DevOps and Release teams during weekly sprints.',
    requirements: ['Experience with basic Linux shell', 'Understanding of basic network subnets', 'Familiarity with Git and GitHub'],
    location: 'Boston Office (Hybrid)',
    status: 'open',
    type: 'internship',
    hoursRequired: 120,
    createdAt: '2026-05-01T08:00:00Z',
    careerCluster: 'Information Technology',
    subcluster: 'Cloud Engineering & DevOps',
    semester: 'Fall 2026'
  },
  {
    id: 'opp-2',
    tenantId: 'charleston',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'Frontend React UI Apprentice',
    description: 'Collaborate with visual engineers to craft semantic, accessible layouts using Tailwind CSS and React 19. Ensure rich contrast ratios and custom screen-reader accessibility landmarks.',
    requirements: ['Intermediate HTML/CSS/JS knowledge', 'Completed initial React core coursework', 'Passion for accessible UI/UX design'],
    location: 'Remote (Northeast Region)',
    status: 'open',
    type: 'apprenticeship',
    hoursRequired: 160,
    createdAt: '2026-05-10T10:00:00Z',
    careerCluster: 'Information Technology',
    subcluster: 'Web Development'
  },
  {
    id: 'opp-3',
    tenantId: 'greenville',
    organizationId: 'org-hospital',
    organizationName: 'Valley Medical Center',
    title: 'Allied Clinical Rotation Aide',
    description: 'Assist nursing staff in patient triage, support record maintenance under direct clinical supervision, and gain foundational credits for pre-med or clinical nursing pathways.',
    requirements: ['Completed First Aid and CPR Certification', 'Basic understanding of medical privacy (HIPAA)', 'Current health screening clearance'],
    location: 'Valley Medical Center Main Campus',
    status: 'open',
    type: 'internship',
    hoursRequired: 200,
    createdAt: '2026-05-15T09:00:00Z',
    careerCluster: 'Health Science',
    subcluster: 'Clinical Nursing'
  },
  {
    id: 'opp-4',
    tenantId: 'richland',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'K-12 IT Support Co-Op',
    description: 'Serve Springfield regional schools in diagnosing technology, deploying ChromeOS fleets, maintaining smartboards, and managing local student networking configuration settings.',
    requirements: ['High school junior or senior status', 'Basic PC hardware knowledge', 'Strong human service and troubleshooting keys'],
    location: 'Springfield Public Schools (Onsite)',
    status: 'open',
    type: 'co-op',
    hoursRequired: 80,
    createdAt: '2026-05-20T11:00:00Z',
    careerCluster: 'Information Technology',
    subcluster: 'IT Support'
  },
  {
    id: 'opp-5',
    tenantId: 'spartanburg',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'Cybersecurity Analyst Shadow',
    description: 'Learn fundamental network monitoring controls, analyze raw alert logs in Splunk, and shadow live threat hunts led by certified CISSPs.',
    requirements: ['Understand TCP/IP protocols', 'Familiarity with Wireshark analysis tools'],
    location: 'Boston Office (Onsite)',
    status: 'open',
    type: 'shadowing',
    hoursRequired: 40,
    createdAt: '2026-05-22T09:30:00Z',
    careerCluster: 'Information Technology',
    subcluster: 'Cybersecurity',
    semester: 'Summer 2026'
  },
  {
    id: 'opp-6',
    tenantId: 'greenville',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'Bioinformatics Lab Tech Rotation',
    description: 'Collaborate with bioinformatics scientists sequence logging analysis, parse raw genetic outputs with Python, and run pipeline cluster tests.',
    requirements: ['Completed introductory biology', 'Understand basic Python variables and lists'],
    location: 'Cambridge Lab Center',
    status: 'open',
    type: 'clinical',
    hoursRequired: 180,
    createdAt: '2026-05-25T14:15:00Z',
    careerCluster: 'Health Science',
    subcluster: 'Biotechnology Research'
  },
  {
    id: 'opp-7',
    tenantId: 'horry',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'Network Systems Operations Co-Op',
    description: 'Design and deploy physical server network topologies, patch cabling, configure central switch VLANs, and perform stress testing.',
    requirements: ['Understanding of network topologies', 'Enthusiasm for hardware deployment'],
    location: 'Boston Office (Onsite)',
    status: 'open',
    type: 'co-op',
    hoursRequired: 120,
    createdAt: '2026-05-28T10:00:00Z',
    careerCluster: 'Information Technology',
    subcluster: 'Network Administration'
  },
  {
    id: 'opp-8',
    tenantId: 'union',
    organizationId: 'org-apex',
    organizationName: 'Precision Dynamics',
    title: 'CNC Machinist Apprentice',
    description: 'Operate state-of-the-art CNC machine tools, perform quality tolerance inspections using high-precision micrometers, and follow industrial safety procedures.',
    requirements: ['Basic technical drawing reading skills', 'High attention to detail', 'Safety-first mindset'],
    location: 'Precision Dynamics Plant (Onsite)',
    status: 'open',
    type: 'apprenticeship',
    hoursRequired: 160,
    createdAt: '2026-06-02T09:00:00Z',
    careerCluster: 'Advanced Manufacturing',
    subcluster: 'Machining Tech'
  },
  {
    id: 'opp-9',
    tenantId: 'charleston',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    title: 'Accounting Associate Assistant',
    description: 'Analyze accounts receivable registers, reconcile monthly bank ledgers, support ledger entry audits, and organize corporate balance sheets.',
    requirements: ['Introductory financial accounting coursework', 'Proficiency with Microsoft Excel/Sheets', 'High organization skill'],
    location: 'Charleston Office (Hybrid)',
    status: 'open',
    type: 'internship',
    hoursRequired: 120,
    createdAt: '2026-06-03T11:30:00Z',
    careerCluster: 'Business & Finance',
    subcluster: 'Accounting'
  },
  {
    id: 'opp-10',
    tenantId: 'spartanburg',
    organizationId: 'org-apex',
    organizationName: 'Precision Dynamics',
    title: 'Automation Engineering Shadow',
    description: 'Shadow mechanical and automation controls engineers on automated assembly systems, PLCs, pneumatic networks, and robotic arm pathing.',
    requirements: ['Interest in robotics/automation', 'Basic understanding of electrical circuits'],
    location: 'Precision Dynamics Plant (Onsite)',
    status: 'open',
    type: 'shadowing',
    hoursRequired: 40,
    createdAt: '2026-06-05T14:00:00Z',
    careerCluster: 'Advanced Manufacturing',
    subcluster: 'Robotics',
    semester: 'Spring 2027'
  }
];

export const MOCK_APPLICATIONS: Application[] = [
  // 3 accepted (approved)
  {
    id: 'app-1',
    tenantId: 'spartanburg',
    opportunityId: 'opp-1',
    opportunityTitle: 'Junior Cloud Infrastructure Intern',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-sam',
    studentName: 'Samuel Carter',
    studentEmail: 'student.sam@example.com',
    status: 'accepted',
    appliedAt: '2026-05-12T14:30:00Z',
    coverLetter: 'I would love to apply for this DevOps opportunity. I recently completed my AWS Cloud Practitioner certification.',
    resumeSummary: 'AWS Certified Cloud Practitioner, 2 High School scripting achievements'
  },
  {
    id: 'app-2',
    tenantId: 'charleston',
    opportunityId: 'opp-2',
    opportunityTitle: 'Frontend React UI Apprentice',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-lily',
    studentName: 'Lily Watson',
    studentEmail: 'lily@example.com',
    status: 'accepted',
    appliedAt: '2026-05-13T10:00:00Z',
    coverLetter: 'I am highly passionate about accessibility and constructing rich visual experiences in React.',
    resumeSummary: 'Completed Advanced Web Dev, proficient with Tailwind CSS and responsive architecture.'
  },
  {
    id: 'app-3',
    tenantId: 'spartanburg',
    opportunityId: 'opp-5',
    opportunityTitle: 'Cybersecurity Analyst Shadow',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-max',
    studentName: 'Max Peterson',
    studentEmail: 'max@example.com',
    status: 'accepted',
    appliedAt: '2026-05-14T09:00:00Z',
    coverLetter: 'Interested in cybersecurity workflows and network packet analysis.',
    resumeSummary: 'Competed in CyberPatriot, familiar with Linux firewalls and basic scripting.'
  },
  // 5 rejected (declined)
  {
    id: 'app-4',
    tenantId: 'spartanburg',
    opportunityId: 'opp-1',
    opportunityTitle: 'Junior Cloud Infrastructure Intern',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'rejected',
    appliedAt: '2026-05-15T08:00:00Z',
    coverLetter: 'Applying for internship.',
    resumeSummary: 'General interest.'
  },
  {
    id: 'app-5',
    tenantId: 'charleston',
    opportunityId: 'opp-2',
    opportunityTitle: 'Frontend React UI Apprentice',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'rejected',
    appliedAt: '2026-05-15T09:00:00Z',
    coverLetter: 'Interested in React.',
    resumeSummary: 'No React experience.'
  },
  {
    id: 'app-6',
    tenantId: 'spartanburg',
    opportunityId: 'opp-5',
    opportunityTitle: 'Cybersecurity Analyst Shadow',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'rejected',
    appliedAt: '2026-05-15T11:00:00Z',
    coverLetter: 'Keen on shadow role.',
    resumeSummary: 'Beginner interest.'
  },
  {
    id: 'app-7',
    tenantId: 'greenville',
    opportunityId: 'opp-6',
    opportunityTitle: 'Bioinformatics Lab Tech Rotation',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'rejected',
    appliedAt: '2026-05-15T12:00:00Z',
    coverLetter: 'Bioinformatics application.',
    resumeSummary: 'No biology coursework.'
  },
  {
    id: 'app-8',
    tenantId: 'horry',
    opportunityId: 'opp-7',
    opportunityTitle: 'Network Systems Operations Co-Op',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'rejected',
    appliedAt: '2026-05-15T13:00:00Z',
    coverLetter: 'Networking co-op.',
    resumeSummary: 'Familiar with wifi.'
  },
  // 1 reviewed (interviewed in UI)
  {
    id: 'app-9',
    tenantId: 'spartanburg',
    opportunityId: 'opp-1',
    opportunityTitle: 'Junior Cloud Infrastructure Intern',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'reviewed',
    appliedAt: '2026-05-16T15:00:00Z',
    coverLetter: 'Ready for tech career interview.',
    resumeSummary: 'Completed intro courses, highly motivated.'
  },
  // 13 pending (applied in UI)
  {
    id: 'app-10',
    tenantId: 'spartanburg',
    opportunityId: 'opp-1',
    opportunityTitle: 'Junior Cloud Infrastructure Intern',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    studentEmail: 'john@example.com',
    status: 'pending',
    appliedAt: '2026-05-17T10:00:00Z',
    coverLetter: 'Pending application.',
    resumeSummary: 'Standard credentials.'
  },
  { id: 'app-11', tenantId: 'spartanburg', opportunityId: 'opp-1', opportunityTitle: 'Junior Cloud Infrastructure Intern', organizationName: 'Apex Tech Solutions', studentId: 'student-sam', studentName: 'Samuel Carter', studentEmail: 'student.sam@example.com', status: 'pending', appliedAt: '2026-05-18T10:00:00Z', coverLetter: 'Interested.', resumeSummary: 'AWS Certified.' },
  { id: 'app-12', tenantId: 'charleston', opportunityId: 'opp-2', opportunityTitle: 'Frontend React UI Apprentice', organizationName: 'Apex Tech Solutions', studentId: 'student-sam', studentName: 'Samuel Carter', studentEmail: 'student.sam@example.com', status: 'pending', appliedAt: '2026-05-18T11:00:00Z', coverLetter: 'React interest.', resumeSummary: 'Beginner UI.' },
  { id: 'app-13', tenantId: 'spartanburg', opportunityId: 'opp-5', opportunityTitle: 'Cybersecurity Analyst Shadow', organizationName: 'Apex Tech Solutions', studentId: 'student-sam', studentName: 'Samuel Carter', studentEmail: 'student.sam@example.com', status: 'pending', appliedAt: '2026-05-18T12:00:00Z', coverLetter: 'Sec shadow.', resumeSummary: 'Familiar with nmap.' },
  { id: 'app-14', tenantId: 'greenville', opportunityId: 'opp-6', opportunityTitle: 'Bioinformatics Lab Tech Rotation', organizationName: 'Apex Tech Solutions', studentId: 'student-sam', studentName: 'Samuel Carter', studentEmail: 'student.sam@example.com', status: 'pending', appliedAt: '2026-05-18T13:00:00Z', coverLetter: 'Bioinformatics.', resumeSummary: 'Interdisciplinary.' },
  { id: 'app-15', tenantId: 'horry', opportunityId: 'opp-7', opportunityTitle: 'Network Systems Operations Co-Op', organizationName: 'Apex Tech Solutions', studentId: 'student-sam', studentName: 'Samuel Carter', studentEmail: 'student.sam@example.com', status: 'pending', appliedAt: '2026-05-18T14:00:00Z', coverLetter: 'NOC Co-Op.', resumeSummary: 'Configured router.' },
  { id: 'app-16', tenantId: 'spartanburg', opportunityId: 'opp-1', opportunityTitle: 'Junior Cloud Infrastructure Intern', organizationName: 'Apex Tech Solutions', studentId: 'student-lily', studentName: 'Lily Watson', studentEmail: 'lily@example.com', status: 'pending', appliedAt: '2026-05-19T08:00:00Z', coverLetter: 'Pending.', resumeSummary: 'Web Dev.' },
  { id: 'app-17', tenantId: 'charleston', opportunityId: 'opp-2', opportunityTitle: 'Frontend React UI Apprentice', organizationName: 'Apex Tech Solutions', studentId: 'student-lily', studentName: 'Lily Watson', studentEmail: 'lily@example.com', status: 'pending', appliedAt: '2026-05-19T09:00:00Z', coverLetter: 'Pending.', resumeSummary: 'HTML.' },
  { id: 'app-18', tenantId: 'spartanburg', opportunityId: 'opp-5', opportunityTitle: 'Cybersecurity Analyst Shadow', organizationName: 'Apex Tech Solutions', studentId: 'student-lily', studentName: 'Lily Watson', studentEmail: 'lily@example.com', status: 'pending', appliedAt: '2026-05-19T10:00:00Z', coverLetter: 'Pending.', resumeSummary: 'Familiar.' },
  { id: 'app-19', tenantId: 'greenville', opportunityId: 'opp-6', opportunityTitle: 'Bioinformatics Lab Tech Rotation', organizationName: 'Apex Tech Solutions', studentId: 'student-max', studentName: 'Max Peterson', studentEmail: 'max@example.com', status: 'pending', appliedAt: '2026-05-19T11:00:00Z', coverLetter: 'Pending.', resumeSummary: 'Pre-med track.' },
  { id: 'app-20', tenantId: 'horry', opportunityId: 'opp-7', opportunityTitle: 'Network Systems Operations Co-Op', organizationName: 'Apex Tech Solutions', studentId: 'student-max', studentName: 'Max Peterson', studentEmail: 'max@example.com', status: 'pending', appliedAt: '2026-05-19T12:00:00Z', coverLetter: 'Pending.', resumeSummary: 'Network cabling.' },
  { id: 'app-21', tenantId: 'spartanburg', opportunityId: 'opp-1', opportunityTitle: 'Junior Cloud Infrastructure Intern', organizationName: 'Apex Tech Solutions', studentId: 'student-max', studentName: 'Max Peterson', studentEmail: 'max@example.com', status: 'pending', appliedAt: '2026-05-19T13:00:00Z', coverLetter: 'Pending.', resumeSummary: 'Linux user.' },
  { id: 'app-22', tenantId: 'charleston', opportunityId: 'opp-2', opportunityTitle: 'Frontend React UI Apprentice', organizationName: 'Apex Tech Solutions', studentId: 'student-max', studentName: 'Max Peterson', studentEmail: 'max@example.com', status: 'pending', appliedAt: '2026-05-19T14:00:00Z', coverLetter: 'Pending.', resumeSummary: 'Web novice.' },

  // ValleyHealth application
  {
    id: 'app-valleyhealth-1',
    tenantId: 'greenville',
    opportunityId: 'opp-3',
    opportunityTitle: 'Allied Clinical Rotation Aide',
    organizationName: 'Valley Medical Center',
    studentId: 'student-alex',
    studentName: 'Alex Rivera',
    studentEmail: 'student.alex@example.com',
    status: 'accepted',
    appliedAt: '2026-05-16T11:00:00Z',
    coverLetter: 'Getting real clinical experience is my dream before starting nursing school.',
    resumeSummary: 'Certified EMT-Basic, HIPAA Credentialed.'
  }
];

export const MOCK_PLACEMENTS: Placement[] = [
  {
    id: 'place-1',
    tenantId: 'spartanburg',
    opportunityId: 'opp-1',
    opportunityTitle: 'Junior Cloud Infrastructure Intern',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-sam',
    studentName: 'Samuel Carter',
    mentorId: 'mentor-mary',
    mentorName: 'Mary Vance (Cloud Architect)',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'active'
  },
  {
    id: 'place-2',
    tenantId: 'charleston',
    opportunityId: 'opp-2',
    opportunityTitle: 'Frontend React UI Apprentice',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-lily',
    studentName: 'Lily Watson',
    mentorId: 'mentor-mary',
    mentorName: 'Mary Vance (Cloud Architect)',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'active'
  },
  {
    id: 'place-3',
    tenantId: 'spartanburg',
    opportunityId: 'opp-5',
    opportunityTitle: 'Cybersecurity Analyst Shadow',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-max',
    studentName: 'Max Peterson',
    mentorId: 'mentor-mary',
    mentorName: 'Mary Vance (Cloud Architect)',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'active'
  },
  {
    id: 'place-4',
    tenantId: 'horry',
    opportunityId: 'opp-7',
    opportunityTitle: 'Network Systems Operations Co-Op',
    organizationId: 'org-apex',
    organizationName: 'Apex Tech Solutions',
    studentId: 'student-john',
    studentName: 'John Doe',
    mentorId: 'mentor-mary',
    mentorName: 'Mary Vance (Cloud Architect)',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'active'
  },
  {
    id: 'place-valleyhealth-1',
    tenantId: 'greenville',
    opportunityId: 'opp-3',
    opportunityTitle: 'Allied Clinical Rotation Aide',
    organizationId: 'org-hospital',
    organizationName: 'Valley Medical Center',
    studentId: 'student-alex',
    studentName: 'Alex Rivera',
    mentorId: 'mentor-mark',
    mentorName: 'Mark Ortiz (Cardiology Lead)',
    startDate: '2026-06-01',
    endDate: '2026-08-31',
    status: 'active'
  }
];

export const MOCK_TASKS: StudentTask[] = [
  {
    id: 'task-1',
    tenantId: 'apextech',
    placementId: 'place-1',
    studentId: 'student-sam',
    title: 'Initialize Local Linux VM Playground',
    description: 'Configure a virtualized Linux server (Ubuntu Server LTS) on your system. Document your user permissions, subnets in `/etc/netplan`, and host file listings.',
    status: 'approved',
    submissionNotes: 'All ports set. Successfully installed Ubuntu utilizing VirtualBox. Created basic cron script reporting active network uptime in logs.',
    submissionLink: 'https://github.com/samc-apex/vm-setup-logs',
    feedback: 'Fantastic documentation, Sam. Your cron configuration was optimal and cleanly encapsulated.',
    rating: 5,
    dueDate: '2026-06-05',
    updatedAt: '2026-06-04T16:00:00Z'
  },
  {
    id: 'task-2',
    tenantId: 'apextech',
    placementId: 'place-1',
    studentId: 'student-sam',
    title: 'Shadow Terraform AWS Setup Run',
    description: 'Participate in Mary Vances AWS infra-setup sprint call. Create a visual architecture markdown block depicting VPC, Subnets, Route Tables, and NAT gateways utilized.',
    status: 'submitted',
    submissionNotes: 'Completed shadowing session. Here is a link to my draft documentation and diagram for the standard multi-AZ development stack VPC.',
    submissionLink: 'https://github.com/samc-apex/infra-shadow-vpc',
    dueDate: '2026-06-12',
    updatedAt: '2026-06-07T11:00:00Z'
  },
  {
    id: 'task-3',
    tenantId: 'apextech',
    placementId: 'place-1',
    studentId: 'student-sam',
    title: 'Implement Shell Automation Script',
    description: 'Write a bash script that audits active directory listings, trims temporary file structures older than 7 days, and reports outcome to central metrics folder.',
    status: 'in_progress',
    dueDate: '2026-06-19',
    updatedAt: '2026-06-06T10:00:00Z'
  },
  {
    id: 'task-4',
    tenantId: 'valleyhealth',
    placementId: 'place-2',
    studentId: 'student-alex',
    title: 'Complete Patient Intake Flow Standards',
    description: 'Document standard hospital safety protocols, patient validation protocols (name, DOB checks), and emergency call handles assigned in nursing wings.',
    status: 'approved',
    submissionNotes: 'Memorized and passed hospital checklist scoring 100%. Highlighted triple-point check standards (Wristband, Charting, Oral confirmation).',
    feedback: 'Outstanding focus on clinical safety. This forms the bedrock of our profession.',
    rating: 5,
    dueDate: '2026-06-04',
    updatedAt: '2026-06-03T14:00:00Z'
  },
  {
    id: 'task-5',
    tenantId: 'valleyhealth',
    placementId: 'place-2',
    studentId: 'student-alex',
    title: 'Demonstrate Cardiology Triage Log Entry',
    description: 'Complete supervised shadow intake for cardiology vitals, demonstrating accurate blood pressure, pulse oximetry, and temperature record entry in simulated patient log charts.',
    status: 'submitted',
    submissionNotes: 'Intake details logged correctly. I assisted the nurse with 5 successful intake vitals logs.',
    dueDate: '2026-06-11',
    updatedAt: '2026-06-07T09:00:00Z'
  }
];

export const MOCK_ATTENDANCE: AttendanceReport[] = [
  {
    id: 'report-1',
    tenantId: 'apextech',
    placementId: 'place-1',
    studentId: 'student-sam',
    studentName: 'Samuel Carter',
    weekEnding: '2026-06-05',
    hoursWorked: 20,
    summary: 'Successfully finished local Linux VM setup. Learned AWS account roles and observed our first cloud infrastructure sprint with Mary Vance. Prepared documentation guidelines.',
    status: 'approved',
    feedback: 'Excellent first week, Sam! Keep diving into the command line options.',
    approvedBy: 'mentor-mary',
    createdAt: '2026-06-05T17:00:00Z'
  },
  {
    id: 'report-2',
    tenantId: 'valleyhealth',
    placementId: 'place-2',
    studentId: 'student-alex',
    studentName: 'Alex Rivera',
    weekEnding: '2026-06-05',
    hoursWorked: 24,
    summary: 'Completed general hospital onboarding. Attended nursing alignment and triaged patients under direct cardiology resident tracking. Confirmed HIPAA clearance.',
    status: 'approved',
    feedback: 'Pristine attendance and strong engagement during cardiology shadow intake cycles.',
    approvedBy: 'mentor-mark',
    createdAt: '2026-06-05T16:30:00Z'
  }
];

export const MOCK_LOGS: SystemLog[] = [
  {
    id: 'log-login-1',
    tenantId: 'apextech',
    userEmail: 'joshua@losonova.com',
    action: 'USER_SIGN_IN',
    details: 'Joshua (superadmin) signed into the portal.',
    timestamp: '2026-06-01T08:50:00Z'
  },
  {
    id: 'log-1',
    tenantId: 'apextech',
    userEmail: 'joshua@losonova.com',
    action: 'CREATE_TENANT',
    details: 'Tenant Apex Tech WBL Portal created and custom CSS theme attributes allocated.',
    timestamp: '2026-06-01T09:00:00Z'
  },
  {
    id: 'log-login-2',
    tenantId: 'apextech',
    userEmail: 'dan.corp@example.com',
    action: 'USER_SIGN_IN',
    details: 'Dan Foster (organization) signed into the portal.',
    timestamp: '2026-06-01T10:15:00Z'
  },
  {
    id: 'log-2',
    tenantId: 'apextech',
    userEmail: 'dan.corp@example.com',
    action: 'CREATE_OPPORTUNITY',
    details: 'Created internship opportunity: Junior Cloud Infrastructure Intern.',
    timestamp: '2026-06-01T10:30:00Z'
  },
  {
    id: 'log-login-3',
    tenantId: 'apextech',
    userEmail: 'student.sam@example.com',
    action: 'USER_SIGN_IN',
    details: 'Samuel Carter (student) signed into the portal.',
    timestamp: '2026-06-04T12:00:00Z'
  },
  {
    id: 'log-3',
    tenantId: 'apextech',
    userEmail: 'student.sam@example.com',
    action: 'SUBMIT_TASK',
    details: 'Submitted assignment: Initialize Local Linux VM Playground.',
    timestamp: '2026-06-04T15:45:00Z'
  },
  {
    id: 'log-login-4',
    tenantId: 'apextech',
    userEmail: 'mentor.mary@example.com',
    action: 'USER_SIGN_IN',
    details: 'Mary Vance (employer) signed into the portal.',
    timestamp: '2026-06-05T16:00:00Z'
  },
  {
    id: 'log-4',
    tenantId: 'apextech',
    userEmail: 'mentor.mary@example.com',
    action: 'APPROVE_ATTENDANCE',
    details: 'Approved Samuel Carters Week Ending 2026-06-05 attendance report (20 hours logged).',
    timestamp: '2026-06-05T17:15:00Z'
  }
];
